import React, { useState } from 'react';
import 'fragments/follow-up-card/follow-up-card-payment/follow-up-card-payment.scss';
import { FollowUpCardPaymentFragmentProps } from 'fragments/follow-up-card/follow-up-card-payment/interfaces';
import { useFollowUpCardPaymentController } from 'fragments/follow-up-card/follow-up-card-payment/follow-up-card-payment.controller';
import { Button, Form, Modal, Space, Tooltip, Typography } from 'antd';
import { ArrowUpOutlined, CheckCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import ModalForm from '../../../components/modal-form/modal-form.component';
import CurrencyInput from 'components/currency-input/currency-input.component';

export const FollowUpCardPaymentFragment: React.FC<FollowUpCardPaymentFragmentProps> = (props) => {
    const { useController = useFollowUpCardPaymentController } = props;
    const controller = useController();
    const { translate } = useTranslator();
    const { Text } = Typography;
    // Only the card of the current month of a periodic contract is driven by the increase schedule.
    const isCurrentPeriodicFollowUp = controller.isCurrentPeriodicFollowUp;
    // Render
    return (
        <div className={'follow-up-card-payment'}>
            <ModalForm
                className={'update-rental-price-modal-form'}
                onFinish={controller.onUpdateFormSubmit}
                form={controller.form}
                isvisible={controller.isUpdateModalFormVisible}
                isLoading={controller.isUpdateFormLoading}
                onCancel={controller.onUpdateFormCancel}
                initialValues={{ price: controller.nextRentalPrice }}
                title={translate({ key: 'follow-ups.update-rental-price' })}
            >
                <Form.Item
                    label={translate({ key: 'follow-ups.price' })}
                    labelAlign="left"
                    labelCol={{ span: '15', offset: '0' }}
                    name="price"
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <CurrencyInput size={'middle'} className="input-rent-amount" placeholder={``} />
                </Form.Item>
            </ModalForm>
            <Modal
                className={'confirm-interest-modal'}
                visible={controller.isInterestModalVisible}
                onCancel={controller.onInterestModalCancel}
                title={translate({ key: 'follow-ups.confirm-interest-title' })}
                footer={[
                    <Button key={'ignore'} onClick={controller.onIgnoreInterestAndPay}>
                        {translate({ key: 'follow-ups.ignore-interest-action' })}
                    </Button>,
                    <Button key={'withInterest'} type={'primary'} onClick={controller.onPayWithInterest}>
                        {translate({ key: 'follow-ups.pay-with-interest-action' })}
                    </Button>,
                ]}
            >
                <Text>{translate({ key: 'follow-ups.interest-price' })} </Text>
                <Text strong>{controller.interestPrice}</Text>
                <br />
                <Text>{translate({ key: 'follow-ups.confirm-interest-body' })}</Text>
            </Modal>
            <Typography.Text strong className="title-card">
                {translate({ key: 'followUp.rental' })}
            </Typography.Text>
            <Space>
                <Tooltip
                    key={1}
                    color={'white'}
                    className={controller.showUpdateNotice ? 'upgradeable-follow-up' : 'hidden-upgradeable-follow-up'}
                    title={
                        <div
                            className={
                                controller.showUpdateNotice ? 'upgradeable-text-warn' : 'hidden-upgradable-text-warn'
                            }
                        >
                            <Text strong>
                                <span className="no-capitalize">
                                    {!isCurrentPeriodicFollowUp
                                        ? translate({ key: 'follow-ups.default-upgrade-price' })
                                        : controller.adjustmentIndex
                                        ? translate({
                                              key: 'follow-ups.upgrade-with-index',
                                              extra: { index: controller.adjustmentIndex },
                                          })
                                        : translate({ key: 'follow-ups.you-are-able-to-update-rental-price' })}
                                </span>
                            </Text>
                            <br />
                            {!isCurrentPeriodicFollowUp ? (
                                <div>
                                    <Text>{translate({ key: 'follow-ups.price-from-next-month' })}</Text>
                                    <Text> ${controller.nextRentalPrice}</Text>
                                </div>
                            ) : (
                                <Text>{translate({ key: 'follow-ups.update-price' })}</Text>
                            )}
                            <br />
                            {controller.showUpdateAction ? (
                                <Button onClick={() => controller.onClickEditButton()} type="link">
                                    {translate({ key: 'follow-ups.update' })}
                                </Button>
                            ) : null}
                        </div>
                    }
                >
                    <ArrowUpOutlined
                        className={
                            controller.showUpdateNotice && isCurrentPeriodicFollowUp
                                ? 'edit-button'
                                : 'hidden-edit-button'
                        }
                    />
                </Tooltip>
                <Form
                    form={controller.rentalForm}
                    onFinish={controller.onEnterPayRent}
                    onBlur={controller.onBlurPayRent}
                >
                    <Form.Item
                        name="amountPaid"
                        initialValue={controller.amountPaid == 0 ? undefined : controller.amountPaid}
                    >
                        <CurrencyInput
                            size={'middle'}
                            className="input-rent-amount"
                            disabled={controller.isInputDisabled}
                            placeholder={`${controller.rentalPrice}`}
                        />
                    </Form.Item>
                </Form>

                {/* Interest decisions are handled exclusively by the modal (ATP-61). The tooltip
                    that previously showed interest info on hover is no longer needed. */}
                <span className={'payment-status-icons'}>
                    <InfoCircleFilled
                        className={controller.paymentStatus === 'incomplete' ? 'not-paid-icon' : 'hidden-not-paid-icon'}
                        onPointerDown={controller.onPaymentActionPointerDown}
                        onPointerUp={controller.onPaymentActionPointerEnd}
                        onPointerCancel={controller.onPaymentActionPointerEnd}
                        onPointerLeave={controller.onPaymentActionPointerEnd}
                        onClick={controller.onMarkAsPaid}
                    />
                    <CheckCircleFilled
                        className={controller.paymentStatus === 'complete' ? 'is-paid-icon' : 'hidden-is-paid-icon'}
                        onPointerDown={controller.onPaymentActionPointerDown}
                        onPointerUp={controller.onPaymentActionPointerEnd}
                        onPointerCancel={controller.onPaymentActionPointerEnd}
                        onPointerLeave={controller.onPaymentActionPointerEnd}
                        onClick={controller.onMarkAsUnpaid}
                    />
                </span>

                <Tooltip
                    color={'white'}
                    title={
                        <Button
                            type="link"
                            onPointerDown={controller.onPaymentActionPointerDown}
                            onPointerUp={controller.onPaymentActionPointerEnd}
                            onPointerCancel={controller.onPaymentActionPointerEnd}
                            onPointerLeave={controller.onPaymentActionPointerEnd}
                            onClick={controller.onMarkAsUnpaid}
                        >
                            {translate({ key: 'follow-ups.mark-as-dont-paid' })}
                        </Button>
                    }
                >
                    <InfoCircleFilled
                        className={
                            controller.paymentStatus === 'partial'
                                ? 'partially-paid-icon'
                                : 'hidden-partially-paid-icon'
                        }
                        onPointerDown={controller.onPaymentActionPointerDown}
                        onPointerUp={controller.onPaymentActionPointerEnd}
                        onPointerCancel={controller.onPaymentActionPointerEnd}
                        onPointerLeave={controller.onPaymentActionPointerEnd}
                        onClick={controller.onMarkAsPaid}
                    />
                </Tooltip>
            </Space>
        </div>
    );
};
